import { Trans, useLingui } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { observer } from "mobx-react-lite";
import { useState } from "react";

import { apiGetPatientsByPatientIdMedicationsOptions } from "../../../api";
import { AppModel } from "../../../app/app-model";
import { AddButton } from "../../../components/ui-kit/add-button";
import { PatientSubpage } from "../common/patient-subpage";
import { useGdmt } from "../gdmt/gdmt";
import { GdmtCard } from "../gdmt/gdmt-card";
import { AddMedicationsDialog } from "./add-medications-dialog";
import { MedicationsDataTable } from "./medications-data-table";

export const MedicationsTable = observer(function MedicationsTable(props: {
  app: AppModel;
}) {
  const { t } = useLingui();
  const patient = props.app.requirePatient();
  const api = props.app.seerlinqApi;
  const [showAddDialog, setShowAddDialog] = useState(false);
  const { data, isPending, refetch } = useQuery(
    apiGetPatientsByPatientIdMedicationsOptions({
      path: { patient_id: patient.patientId },
    }),
  );
  const gdmt = useGdmt(patient.patientId);

  // The GDMT score is computed from the medication list, so refresh it
  // whenever the table's data changes.
  const refetchAll = async () => {
    await Promise.all([refetch(), gdmt.refetch()]);
  };

  return (
    // Also wait for GDMT so the card doesn't pop in above the already-rendered
    // table and shift it down.
    <PatientSubpage title={t`Medications`} loading={isPending || gdmt.loading}>
      {gdmt.result && <GdmtCard result={gdmt.result} />}

      <MedicationsDataTable
        api={api}
        patient={patient}
        data={data?.medications ?? []}
        onInvalidated={refetchAll}
        persistState
        filterActions={
          <AddButton onClick={() => setShowAddDialog(true)}>
            <Trans>Add medications</Trans>
          </AddButton>
        }
      />

      {showAddDialog && (
        <AddMedicationsDialog
          app={props.app}
          onClose={() => setShowAddDialog(false)}
          onSaved={refetchAll}
        />
      )}
    </PatientSubpage>
  );
});
